// Dashboard.jsx - The morning screen. It answers one question: what needs a person
// today, in what order.
//
// Everything here is a queue, not a chart. Charts are for reviewing a quarter;
// this screen is for clearing a day.

import { Link } from "react-router";
import clsx from "clsx";

import { useAuth } from "./AdminApp.jsx";
import { useAsync, useLocal } from "../lib/hooks.js";
import { ordersApi, adminApi } from "../lib/api.js";
import { money, ago, dayTime, HEARD_LABELS } from "../lib/format.js";
import { useInstall } from "../lib/pwa.js";
import { Panel, Loading, Alert, Stat, Empty, Tag, Button } from "../ui/kit.jsx";
import { ArrowRight, Flag, Inbox, Scale, Dollar, Truck, CheckCircle, Download, Close } from "../ui/icons.jsx";

////////// QUEUES //////////
// Each row is a status that means "somebody has to do the next thing". The order
// is the order they should be worked in: money in the building first, then money
// going out, then the top of the funnel.

const QUEUES = [
  { status: "received", label: "Packages to open", detail: "Film the opening before anything else", icon: Truck, tone: "critical" },
  { status: "evaluating", label: "Being evaluated", detail: "Weighed but not yet quoted", icon: Scale, tone: "caution" },
  { status: "accepted", label: "Waiting on payment", detail: "Customer said yes, money not sent", icon: Dollar, tone: "critical" },
  { status: "declined", label: "To be returned", detail: "Ship back insured, at our cost", icon: Truck, tone: "caution" },
  { status: "vetting", label: "Requests to review", detail: "Approve or decline a kit", icon: Flag, tone: "neutral" },
  { status: "offer_made", label: "Offers out", detail: "Waiting on the customer", icon: Inbox, tone: "neutral" },
];

////////// INSTALL PROMPT //////////
// Offered once, on the screen everybody opens first, and only on the size of
// screen where it is worth doing. Dismissing it is permanent on that device:
// somebody who works from a browser tab has made a decision, not a mistake.

function InstallNudge() {
  const { canPrompt, installed, platform, promptInstall } = useInstall();
  const [dismissed, setDismissed] = useLocal("igs.install.dismissed", false);

  if (installed || dismissed || (!canPrompt && platform !== "ios")) return null;

  return (
    <Panel quiet className="lg:hidden flex items-center gap-3 p-3.5">
      <Download size={18} className="text-bronze shrink-0" />
      <p className="text-[13.5px] text-ink-soft leading-snug flex-1 min-w-0">
        Add the dashboard to your home screen. It opens full screen and can send you notifications.
      </p>
      {canPrompt ? (
        <Button size="sm" onClick={promptInstall} className="shrink-0">Install</Button>
      ) : (
        <Link to="/admin/settings?tab=device" className="btn btn-sm btn-quiet shrink-0">How</Link>
      )}
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="shrink-0 text-faint hover:text-ink p-1"
        aria-label="Do not offer this again"
      >
        <Close size={16} />
      </button>
    </Panel>
  );
}

////////// HOW THEY FOUND US //////////
// A bar each, longest first, with what that channel has actually paid out
// beside it. Bars are drawn against the biggest row rather than the total, so
// the shape is readable when one channel dominates, which it usually does.

function HeardBars({ rows }) {
  const most = Math.max(...rows.map((row) => row.count), 1);

  return (
    <ul className="space-y-2.5">
      {rows.map((row) => (
        <li key={row.source}>
          <div className="flex items-baseline justify-between gap-4 mb-1">
            <span className="text-[13.5px] text-ink">{HEARD_LABELS[row.source] || row.source}</span>
            <span className="text-[13px] text-muted num shrink-0">
              {row.count}
              {row.paidOut > 0 && <span className="text-faint"> &middot; {money(row.paidOut)} paid</span>}
            </span>
          </div>
          <div className="h-1.5 bg-surface rounded-full overflow-hidden">
            <div className="h-full bg-bronze rounded-full" style={{ width: `${(row.count / most) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data: stats, loading, error } = useAsync(() => ordersApi.stats(), []);
  const { data: recent } = useAsync(() => ordersApi.list({ limit: 8, sort: "updatedAt" }), []);
  const { data: inbox } = useAsync(() => adminApi.messages({ limit: 5, unread: "true" }).catch(() => null), []);

  if (loading) return <Loading label="Loading today" />;
  if (error) return <div className="p-6"><Alert tone="error">{error.message}</Alert></div>;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-7 sm:space-y-8 max-w-[1400px]">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{dayTime(new Date())}</p>
          <h1 className="display display-lg text-ink mt-1">{greeting}, {user.name.split(" ")[0]}.</h1>
        </div>
        <Link to="/admin/orders" className="btn btn-quiet btn-sm shrink-0">All orders <ArrowRight size={15} /></Link>
      </header>

      <InstallNudge />

      {stats.spot?.stale && (
        <Alert tone="warning" title="The metal price feed is not updating">
          Prices last read {ago(stats.spot.at)} from {stats.spot.source}. Do not send an offer until it recovers,
          or set a manual price in settings.
        </Alert>
      )}

      {/* Queues */}
      <section>
        <h2 className="eyebrow mb-3">What needs doing</h2>
        <div className="grid grid-cols-2 gap-px bg-rule border border-rule lg:grid-cols-3">
          {QUEUES.map((queue) => {
            const count = stats.counts?.[queue.status] || 0;
            const Glyph = queue.icon;
            return (
              <Link
                key={queue.status}
                to={`/admin/orders?status=${queue.status}`}
                className={clsx(
                  "bg-paper p-4 sm:p-5 group transition-colors hover:bg-surface active:bg-surface",
                  count === 0 && "opacity-55"
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Glyph size={16} className={count ? "text-bronze" : "text-faint"} />
                      <p className="text-[13.5px] sm:text-[14px] font-semibold text-ink leading-tight">{queue.label}</p>
                    </div>
                    {/* The one line of instruction is what makes the tile a job
                        rather than a statistic. It is the first thing to go when
                        the tile is half the width of a phone. */}
                    <p className="hint mt-1 leading-snug hidden xs:block">{queue.detail}</p>
                  </div>
                  <span className={clsx(
                    "display text-[1.75rem] sm:text-[2rem] leading-none num shrink-0",
                    count === 0 ? "text-faint" : queue.tone === "critical" ? "text-critical" : queue.tone === "caution" ? "text-caution" : "text-ink"
                  )}>
                    {count}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Last 30 days */}
      <section>
        <h2 className="eyebrow mb-3">Last thirty days</h2>
        <Panel className="grid grid-cols-2 gap-5 sm:gap-6 sm:grid-cols-4 p-5 sm:p-6">
          <Stat label="Kit requests" value={stats.last30.requests} />
          <Stat label="Offers made" value={stats.last30.offersMade} />
          <Stat
            label="Acceptance rate"
            value={stats.last30.acceptanceRate != null ? `${stats.last30.acceptanceRate}%` : "—"}
            tone={stats.last30.acceptanceRate >= 70 ? "positive" : undefined}
          />
          <Stat label="Paid out" value={money(stats.last30.paidOut || 0)} />
        </Panel>
      </section>

      {/* Where customers say they came from. Ranked by how many people said it,
          because paid out is zero until an order finishes and ranking on it
          would bury a channel's first month whatever it sent. The money is
          shown beside each bar instead, which is the number that decides where
          to spend once there is enough of it to mean anything. */}
      {stats.last30.heard?.length > 0 && (
        <section>
          <div className="flex items-baseline justify-between gap-4 mb-3">
            <h2 className="eyebrow">How they found us</h2>
            <span className="hint">
              {stats.last30.heardAnswered} of {stats.last30.requests} answered
            </span>
          </div>
          <Panel className="p-5 sm:p-6">
            <HeardBars rows={stats.last30.heard} />
            <p className="hint mt-4 leading-relaxed">
              What customers chose on the confirmation page. It is optional, so this is a sample
              rather than a census, and it will disagree with the ad platforms. Both are worth
              having: a click id knows which ad was touched last, a person knows what sent them.
            </p>
          </Panel>
        </section>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        {/* Recently touched */}
        <section>
          <div className="flex items-baseline justify-between gap-4 mb-3">
            <h2 className="eyebrow">Recently updated</h2>
            <Link to="/admin/orders" className="text-[13px] text-muted hover:text-ink">See all</Link>
          </div>
          <Panel>
            {recent?.items?.length ? (
              <ul className="divide-y divide-rule-soft">
                {recent.items.map((order) => (
                  <li key={order.id}>
                    <Link to={`/admin/orders/${order.number}`} className="flex items-center gap-3 sm:gap-4 px-4 py-3 hover:bg-surface active:bg-surface transition-colors">
                      <span className="min-w-0 flex-1">
                        <span className="block text-[14px] font-medium text-ink truncate">{order.name || "No name yet"}</span>
                        <span className="block text-[12.5px] text-faint num">{order.number} &middot; {ago(order.updatedAt)}</span>
                      </span>
                      {/* The money and the status sit one above the other on a
                          phone, where three columns would each be too narrow to
                          read. */}
                      <span className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-4 shrink-0">
                        {order.offer ? <span className="num text-[14px] text-ink font-semibold">{money(order.offer)}</span> : null}
                        <Tag tone={order.status === "accepted" ? "positive" : order.vettingFlags?.length ? "caution" : "neutral"}>
                          {order.statusLabel}
                        </Tag>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty title="Nothing yet" icon={Inbox}>Orders will appear here as they come in.</Empty>
            )}
          </Panel>
        </section>

        {/* Unread messages */}
        <section>
          <div className="flex items-baseline justify-between gap-4 mb-3">
            <h2 className="eyebrow">Unread messages</h2>
            <Link to="/admin/inbox" className="text-[13px] text-muted hover:text-ink">Open inbox</Link>
          </div>
          <Panel>
            {inbox?.items?.length ? (
              <ul className="divide-y divide-rule-soft">
                {inbox.items.map((message) => (
                  <li key={message._id} className="px-4 py-3">
                    <p className="text-[13px] font-semibold text-ink num">{message.from || message.orderNumber || "Unknown"}</p>
                    <p className="text-[13.5px] text-muted mt-0.5 line-clamp-2 leading-snug">{message.body}</p>
                    <p className="text-[12px] text-faint mt-1">{ago(message.createdAt)}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty title="All caught up" icon={CheckCircle} className="py-10">
                Nothing waiting for a reply.
              </Empty>
            )}
          </Panel>
        </section>
      </div>
    </div>
  );
}
